import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { amountToWordsSoles } from './amountToWords';
import { getDocumentTypeLabel } from './documentTypes';
import { getLinkedRequirementCodes } from './purchaseOrderRequirements';

const TEAL = [13, 148, 136];
const GRAY = [55, 65, 81];
const GRAY_MUTED = [107, 114, 128];
const GRAY_LIGHT = [156, 163, 175];
const BG_TABLE_ALT = [249, 250, 251];
const MARGIN = 14;

const fmtMoney = (n) =>
  parseFloat(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtUnitPrice = (p) =>
  parseFloat(p || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 4 });

const fmtQty = (value) =>
  parseFloat(value || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 3 });

function formatDate(value) {
  if (!value) return '';
  return new Date(value).toLocaleDateString('es-PE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

function getCurrencySymbol(document) {
  return document?.currency === 'USD' ? '$' : 'S/';
}

function getStatusInfo(document) {
  const map = {
    issued: { label: 'Emitido', tone: 'issued' },
    open: { label: 'Abierto', tone: 'open' },
    completed: { label: 'Completado', tone: 'completed' },
    cancelled: { label: 'Cancelado', tone: 'cancelled' },
  };
  return map[document.status] || { label: document.status || '—', tone: 'issued' };
}

function buildPaymentCondition(document) {
  if (document.payment_condition_type === 'cash') return 'Al contado';
  if (document.payment_condition_type === 'days') {
    return `Cada ${document.payment_condition_days} días`;
  }
  if (document.payment_condition_type === 'date' && document.payment_condition_date) {
    return `Fecha específica: ${formatDate(document.payment_condition_date)}`;
  }
  return '-';
}

function docTypeLabel(supplier) {
  if (supplier?.document_type === 'ruc') return 'RUC';
  if (supplier?.document_type === 'dni') return 'DNI';
  if (supplier?.document_type) return String(supplier.document_type).toUpperCase();
  return 'Documento';
}

function measureSegments(doc, segments) {
  let width = 0;
  segments.forEach((seg) => {
    doc.setFont('helvetica', seg.style === 'label' ? 'bold' : 'normal');
    doc.setFontSize(9);
    width += doc.getTextWidth(seg.text) + (seg.style === 'sep' ? 2.5 : 1.2);
  });
  return width;
}

function drawSegments(doc, startX, y, segments) {
  let x = startX;
  segments.forEach((seg) => {
    if (seg.style === 'label') {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...TEAL);
    } else if (seg.style === 'sep') {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...GRAY_LIGHT);
    } else {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...GRAY);
    }
    doc.setFontSize(9);
    doc.text(seg.text, x, y);
    x += doc.getTextWidth(seg.text) + (seg.style === 'sep' ? 2.5 : 1.2);
  });
  return x;
}

function drawStatusBadge(doc, x, y, statusText, tone) {
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  const padX = 2.5;
  const textW = doc.getTextWidth(statusText);
  const badgeW = textW + padX * 2;
  const badgeH = 5;

  const tones = {
    issued: { fill: [209, 250, 229], text: [6, 95, 70] },
    open: { fill: [254, 243, 199], text: [146, 64, 14] },
    completed: { fill: [219, 234, 254], text: [30, 64, 175] },
    cancelled: { fill: [254, 226, 226], text: [153, 27, 27] },
  };
  const colors = tones[tone] || tones.issued;

  doc.setFillColor(...colors.fill);
  doc.setTextColor(...colors.text);
  doc.roundedRect(x, y - badgeH + 1.2, badgeW, badgeH, 1.2, 1.2, 'F');
  doc.text(statusText, x + padX, y);
  return x + badgeW + 2;
}

function drawSectionTitle(doc, margin, y, title) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...TEAL);
  doc.text(title.toUpperCase(), margin, y);
  doc.setDrawColor(...TEAL);
  doc.setLineWidth(0.4);
  doc.line(margin, y + 1.5, margin + doc.getTextWidth(title.toUpperCase()), y + 1.5);
}

function getItemDescription(item) {
  return item.item_type === 'product'
    ? item.product?.name || item.description || 'N/A'
    : item.service?.name || item.description || 'N/A';
}

function getItemUnit(item) {
  return (
    item.unit_of_measure
    || (item.item_type === 'product' ? item.product?.unit_of_measure : null)
    || '—'
  );
}

function getItemProject(item) {
  if (!item.project) return '—';
  return `${item.project.code || ''}${item.project.code && item.project.name ? ' - ' : ''}${item.project.name || ''}`;
}

function buildItemRows(document, currencySymbol) {
  const rows = (document.items || []).map((item) => [
    getItemDescription(item),
    getItemUnit(item),
    getItemProject(item),
    fmtQty(item.quantity),
    `${currencySymbol} ${fmtUnitPrice(item.unit_price)}`,
    `${currencySymbol} ${fmtMoney(item.subtotal)}`,
  ]);

  (document.activos || []).forEach((activo) => {
    const qty = parseFloat(activo.acquisition_quantity) || 1;
    const unitPrice = parseFloat(activo.acquisition_unit_price) || 0;
    const lineSubtotal = Math.round(qty * unitPrice * 100) / 100;
    const classification = [activo.family?.subcategory?.category?.name, activo.family?.subcategory?.name, activo.family?.name]
      .filter(Boolean)
      .join(' › ');

    let desc = activo.name || 'Activo';
    if (activo.code) desc += `\n${activo.code}`;
    if (classification) desc += `\n${classification}`;

    rows.push([
      desc,
      'UN',
      '—',
      fmtQty(qty),
      `${currencySymbol} ${fmtUnitPrice(unitPrice)}`,
      `${currencySymbol} ${fmtMoney(lineSubtotal)}`,
    ]);
  });

  return rows;
}

function guideTypeLabel(type) {
  const map = {
    entrada: 'Entrada',
    salida: 'Salida',
    inicial: 'Inicial',
    traspaso: 'Traspaso',
    recepcion: 'Recepción',
  };
  return map[type] || type || '—';
}

function formatWarehouseRef(warehouse) {
  if (!warehouse) return null;
  const name = warehouse.name?.trim();
  const code = warehouse.code?.trim();
  if (name) return code ? `Alm: ${name} (${code})` : `Alm: ${name}`;
  if (code) return `Alm: ${code}`;
  return null;
}

function buildReferenceRows(document) {
  const rows = [];

  if (
    (document.document_type === 'nota_credito' || document.document_type === 'nota_debito')
    && (document.reference_series || document.reference_number)
  ) {
    const refNumber =
      document.reference_series && document.reference_number
        ? `${document.reference_series}-${document.reference_number}`
        : document.reference_series || document.reference_number || '—';
    const refDetail = [
      document.reference_document_type
        ? getDocumentTypeLabel(document.reference_document_type)
        : null,
      document.reference_document_date ? formatDate(document.reference_document_date) : null,
    ]
      .filter(Boolean)
      .join(' · ');
    rows.push(['Documento ref.', refNumber, refDetail || '—']);
  }

  const reqByCode = new Map();
  (document.requirements || []).forEach((req) => {
    if (req?.code) reqByCode.set(req.code, req);
  });
  getLinkedRequirementCodes(document).forEach((code) => {
    if (!reqByCode.has(code)) reqByCode.set(code, { code });
  });
  [...reqByCode.values()].forEach((req) => {
    rows.push(['Requerimiento', req.code || `REQ-${req.id}`, '—']);
  });

  (document.purchase_orders || []).forEach((po) => {
    const detail = [
      po.supplier?.name,
      po.order_date ? formatDate(po.order_date) : null,
      po.total_amount != null ? `Total: S/ ${fmtMoney(po.total_amount)}` : null,
    ]
      .filter(Boolean)
      .join(' · ');
    rows.push(['Orden de compra', po.code || '—', detail || '—']);
  });

  (document.service_orders || []).forEach((so) => {
    const detail = [
      so.supplier?.name,
      so.order_date ? formatDate(so.order_date) : null,
      so.total_amount != null ? `Total: S/ ${fmtMoney(so.total_amount)}` : null,
    ]
      .filter(Boolean)
      .join(' · ');
    rows.push(['Orden de servicio', so.code || '—', detail || '—']);
  });

  (document.entry_guides || []).forEach((guide) => {
    const detail = [
      guideTypeLabel(guide.guide_type),
      formatWarehouseRef(guide.warehouse),
      guide.issue_date ? formatDate(guide.issue_date) : null,
    ]
      .filter(Boolean)
      .join(' · ');
    rows.push(['Guía', guide.code || '—', detail || '—']);
  });

  (document.continuous_services || []).forEach((cs) => {
    const detail = [
      cs.supplier?.name,
      cs.start_date ? formatDate(cs.start_date) : null,
      cs.status === 'closed' ? 'Cerrado' : cs.status === 'open' ? 'Abierto' : cs.status,
    ]
      .filter(Boolean)
      .join(' · ');
    rows.push(['Servicio continuo', cs.code || '—', detail || '—']);
  });

  return rows;
}

function buildReferenceLineSegments(document) {
  return buildReferenceRows(document).map(([tipo, codigo, detalle]) => {
    const segments = [
      { text: `${tipo}:`, style: 'label' },
      { text: codigo, style: 'value' },
    ];
    if (detalle && detalle !== '—') {
      segments.push({ text: '·', style: 'sep' }, { text: detalle, style: 'value' });
    }
    return segments;
  });
}

function drawReferencesSection(doc, document, startY, pageWidth, pageHeight) {
  const lineSegments = buildReferenceLineSegments(document);
  if (!lineSegments.length) return startY;

  let y = startY;
  const contentWidth = pageWidth - MARGIN * 2;
  const lineHeight = 4.2;

  if (y + 10 > pageHeight - MARGIN) {
    doc.addPage();
    y = MARGIN + 8;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...TEAL);
  doc.text('Referencias', MARGIN, y);
  doc.setDrawColor(...TEAL);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, y + 1.2, MARGIN + doc.getTextWidth('Referencias'), y + 1.2);
  y += 5.5;

  lineSegments.forEach((segments) => {
    if (y + lineHeight > pageHeight - MARGIN) {
      doc.addPage();
      y = MARGIN + 8;
    }

    if (measureSegments(doc, segments) > contentWidth) {
      const fullLine = segments
        .map((seg) => (seg.style === 'sep' ? ` ${seg.text} ` : seg.text))
        .join('')
        .replace(/:  /, ': ');
      const wrapped = doc.splitTextToSize(fullLine, contentWidth);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...GRAY);
      doc.text(wrapped.slice(0, 3), MARGIN, y);
      y += wrapped.length * lineHeight;
      return;
    }

    drawSegments(doc, MARGIN, y, segments);
    y += lineHeight;
  });

  return y + 3;
}

function getAmountsRightX(doc) {
  const subtotalCol = doc.lastAutoTable?.table?.columns?.[5];
  if (subtotalCol) return subtotalCol.x + subtotalCol.width - 3;
  return MARGIN + 72 + 14 + 32 + 14 + 20 + 30 - 3;
}

function drawFooter(doc, document, startY, pageWidth, pageHeight, amountsRightX, currencySymbol) {
  const contentWidth = pageWidth - MARGIN * 2;
  const obsWidth = contentWidth * 0.52;
  const totalsWidth = contentWidth * 0.44;
  const gap = contentWidth - obsWidth - totalsWidth;
  const totalsX = MARGIN + obsWidth + gap;
  const footerHeight = 28;

  let y = startY;
  if (y + footerHeight > pageHeight - MARGIN) {
    doc.addPage();
    y = MARGIN + 8;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(...TEAL);
  doc.text('Observaciones:', MARGIN, y + 5);

  if (document.notes) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...GRAY);
    const noteLines = doc.splitTextToSize(String(document.notes), obsWidth);
    doc.text(noteLines.slice(0, 4), MARGIN, y + 10);
  }

  const igvRate = parseFloat(document.igv_rate ?? 18);
  let ty = y + 5;
  const lineH = 4.8;

  const drawTotalLine = (label, value, { bold = false, italic = false } = {}) => {
    doc.setFont('helvetica', bold ? 'bold' : italic ? 'italic' : 'normal');
    doc.setFontSize(bold ? 9 : 8);
    doc.setTextColor(...(bold ? TEAL : GRAY_MUTED));
    doc.text(label, totalsX, ty);
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setTextColor(...GRAY);
    doc.text(value, amountsRightX, ty, { align: 'right' });
    ty += lineH;
  };

  drawTotalLine('Base:', `${currencySymbol} ${fmtMoney(document.base_imponible)}`);
  drawTotalLine(`IGV (${igvRate}%):`, `${currencySymbol} ${fmtMoney(document.igv_amount)}`);
  if (parseFloat(document.no_gravados || 0) > 0) {
    drawTotalLine('No grav.:', `${currencySymbol} ${fmtMoney(document.no_gravados)}`);
  }
  drawTotalLine('Total:', `${currencySymbol} ${fmtMoney(document.total_amount)}`, { bold: true });

  if (document.currency !== 'USD') {
    const words = amountToWordsSoles(document.total_amount);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7);
    doc.setTextColor(...GRAY_MUTED);
    const wordLines = doc.splitTextToSize(`Son: ${words}`, amountsRightX - totalsX);
    doc.text(wordLines.slice(0, 2), amountsRightX, ty + 1, { align: 'right' });
  }
}

export async function downloadDocumentPdf(document) {
  if (!document) {
    throw new Error('No hay datos del documento para generar el PDF');
  }

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const currencySymbol = getCurrencySymbol(document);
  const status = getStatusInfo(document);

  let y = MARGIN + 4;

  const title = getDocumentTypeLabel(document.document_type, { uppercase: true });
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(17);
  doc.setTextColor(...TEAL);
  doc.text(title, pageWidth / 2, y, { align: 'center' });

  y += 4;
  doc.setDrawColor(...TEAL);
  doc.setLineWidth(0.7);
  doc.line(MARGIN, y, pageWidth - MARGIN, y);
  y += 8;

  const seriesNumber =
    document.series && document.number
      ? `${document.series}-${document.number}`
      : document.series || document.number || '—';

  const line1 = [
    { text: 'Documento:', style: 'label' },
    { text: document.code || '-', style: 'value' },
    { text: '·', style: 'sep' },
    { text: 'Serie/Núm.:', style: 'label' },
    { text: seriesNumber, style: 'value' },
    { text: '·', style: 'sep' },
    { text: 'Fecha:', style: 'label' },
    { text: formatDate(document.issue_date) || '—', style: 'value' },
    { text: '·', style: 'sep' },
    { text: 'Estado:', style: 'label' },
  ];
  const estadoX = MARGIN + measureSegments(doc, line1);
  drawSegments(doc, MARGIN, y, line1);
  drawStatusBadge(doc, estadoX, y, status.label, status.tone);

  y += 6;
  const supplierLine = [
    { text: 'Proveedor:', style: 'label' },
    { text: document.supplier?.name || 'N/A', style: 'value' },
  ];
  if (document.supplier?.document_number) {
    supplierLine.push(
      { text: '·', style: 'sep' },
      { text: `${docTypeLabel(document.supplier)}:`, style: 'label' },
      { text: document.supplier.document_number, style: 'value' },
    );
  }
  if (document.currency) {
    supplierLine.push(
      { text: '·', style: 'sep' },
      { text: 'Moneda:', style: 'label' },
      { text: document.currency === 'USD' ? 'USD' : 'PEN', style: 'value' },
    );
  }
  drawSegments(doc, MARGIN, y, supplierLine);

  y += 6;
  const paymentLine = [
    { text: 'Condición de pago:', style: 'label' },
    { text: buildPaymentCondition(document), style: 'value' },
  ];
  if (document.payment_date) {
    paymentLine.push(
      { text: '·', style: 'sep' },
      { text: 'Fecha de pago:', style: 'label' },
      { text: formatDate(document.payment_date), style: 'value' },
    );
  }
  drawSegments(doc, MARGIN, y, paymentLine);

  y += 6;
  y = drawReferencesSection(doc, document, y, pageWidth, pageHeight);

  y += 6;
  drawSectionTitle(doc, MARGIN, y, 'Items del Documento');
  y += 6;

  const tableBody = buildItemRows(document, currencySymbol);
  if (tableBody.length === 0) {
    tableBody.push(['Sin ítems registrados', '—', '—', '—', '—', '—']);
  }

  autoTable(doc, {
    startY: y,
    head: [['Descripción', 'Unidad', 'Proyecto', 'Cant.', 'P. Unit', 'Subtotal']],
    body: tableBody,
    margin: { left: MARGIN, right: MARGIN },
    theme: 'grid',
    styles: {
      font: 'helvetica',
      fontSize: 8,
      cellPadding: { top: 3, right: 3, bottom: 3, left: 3 },
      textColor: GRAY,
      lineColor: [229, 231, 235],
      lineWidth: 0.2,
      valign: 'top',
    },
    headStyles: {
      fillColor: TEAL,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 7.5,
      halign: 'left',
    },
    columnStyles: {
      0: { cellWidth: 72 },
      1: { cellWidth: 14, halign: 'center' },
      2: { cellWidth: 32 },
      3: { cellWidth: 14, halign: 'right' },
      4: { cellWidth: 20, halign: 'right' },
      5: { cellWidth: 30, halign: 'right', fontStyle: 'bold' },
    },
    alternateRowStyles: { fillColor: BG_TABLE_ALT },
    didDrawPage: (data) => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...GRAY_LIGHT);
      doc.text(
        `Documento ${document.code || ''} — Pág. ${data.pageNumber}`,
        pageWidth / 2,
        pageHeight - 6,
        { align: 'center' },
      );
    },
  });

  const tableEndY = doc.lastAutoTable.finalY + 8;
  const amountsRightX = getAmountsRightX(doc);
  drawFooter(doc, document, tableEndY, pageWidth, pageHeight, amountsRightX, currencySymbol);

  const safeCode = String(document.code || document.id || 'documento').replace(/[^\w.-]+/g, '_');
  doc.save(`Documento_${safeCode}.pdf`);
}
